import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

type MFAReminderContextType = {
  showMFAReminder: boolean;
  setShowMFAReminder: (show: boolean) => void;
  dismissReminder: (remindAgain?: boolean) => void;
  shouldShowReminder: () => boolean;
};

const MFAReminderContext = createContext<MFAReminderContextType>({
  showMFAReminder: false,
  setShowMFAReminder: () => {},
  dismissReminder: () => {},
  shouldShowReminder: () => false,
});

export const useMFAReminder = () => useContext(MFAReminderContext);

type MFAReminderProviderProps = {
  children: ReactNode;
};

export const MFAReminderProvider = ({ children }: MFAReminderProviderProps) => {
  const [showMFAReminder, _setShowMFAReminder] = useState(false);
  const setShowMFAReminder = useCallback((show: boolean) => {
    if (show) sessionStorage.setItem("mfa_reminder_shown_session", "1");
    _setShowMFAReminder(show);
  }, []);

  const shouldShowReminder = useCallback(() => {
    // Only show once per browser session to avoid clashing with other modals
    if (sessionStorage.getItem("mfa_reminder_shown_session") === "1") {
      return false;
    }

    // First-seen grace period: don't nag brand-new users for 3 days
    const firstSeenKey = "mfa_reminder_first_seen";
    const firstSeen = localStorage.getItem(firstSeenKey);
    if (!firstSeen) {
      localStorage.setItem(firstSeenKey, String(Date.now()));
      return false;
    }
    const daysSinceFirstSeen =
      (Date.now() - Number(firstSeen)) / (1000 * 60 * 60 * 24);
    if (daysSinceFirstSeen < 3) return false;

    // Check if user has dismissed reminder recently
    const dismissed = localStorage.getItem("mfa_reminder_dismissed");
    if (!dismissed) return true;

    try {
      const { timestamp, remindAgain } = JSON.parse(dismissed);
      if (!remindAgain) return false;
      const daysSinceDismissal =
        (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
      return daysSinceDismissal >= 30;
    } catch {
      return true;
    }
  }, []);

  const dismissReminder = useCallback((remindAgain = true) => {
    const dismissalData = {
      timestamp: Date.now(),
      remindAgain,
    };
    localStorage.setItem(
      "mfa_reminder_dismissed",
      JSON.stringify(dismissalData),
    );
    setShowMFAReminder(false);
  }, []);

  const value = useMemo(
    () => ({
      showMFAReminder,
      setShowMFAReminder,
      dismissReminder,
      shouldShowReminder,
    }),
    [showMFAReminder, dismissReminder, shouldShowReminder],
  );

  return (
    <MFAReminderContext.Provider value={value}>
      {children}
    </MFAReminderContext.Provider>
  );
};
